import { Player, PlayerAPI, PlayerConfig, SourceConfig } from 'bitmovin-player';
import { UIFactory, UIManager } from 'bitmovin-player-ui';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { DRMTYPE } from '../../../utils/types';

dayjs.extend(utc);

const TIMESHIFT_4S = '-4s';
const TIMESHIFT_4S_V2 = '-4s-v2';
const ROBUSTNESS = 'SW_SECURE_CRYPTO';

/**
 * The `PlayerWrapper` class provides a wrapper around the video player and its UI.
 * It allows for easy initialization, configuration, and management of the video player.
 *
 * @remarks
 * This class is designed to work with a video player that supports HLS and DRM (Digital Rights Management).
 *
 * @example
 * ```typescript
 * const container = document.getElementById('video-container');
 * const config: PlayerConfig = { player configuration };
 * const playerWrapper = new PlayerWrapper(container, config);
 *
 * playerWrapper.init(
 *   'https://example.com/video.m3u8',
 *   '2023-01-01T00:00:00Z',
 *   '2023-01-01T01:00:00Z',
 *   true,
 *   DRMTYPE.WIDEVINE,
 *   'https://example.com/license'
 * );
 * ```
 */
export class PlayerWrapper {
	autoplay = false;
	drmType?: DRMTYPE;
	laSource?: string;
	player: PlayerAPI;
	playerUI: UIManager;
	source = '';
	timeShift?: boolean = false;
	videoEnd = '';
	videoStart = '';

	/**
	 * Creates an instance of the VideoPlayer.
	 *
	 * @param container - The HTML element that will contain the video player.
	 * @param config - The configuration object for the player.
	 */
	constructor(container: HTMLElement, config: PlayerConfig) {
		this.player = new Player(container, config);
		this.playerUI = UIFactory.buildDefaultUI(this.player);
	}

	/**
	 * Initializes the video player with the provided parameters.
	 *
	 * @param {string} source - The source URL of the video.
	 * @param {string} videoStart - The start time of the video in ISO 8601 format.
	 * @param {string} videoEnd - The end time of the video in ISO 8601 format.
	 * @param {boolean} [autoplay=true] - Whether the video should autoplay. Defaults to true.
	 * @param {DRMTYPE} [drmType] - The type of DRM (Digital Rights Management) to be used, if any.
	 * @param {string} [laSource] - The license acquisition source URL, if DRM is used.
	 * @returns {void}
	 */
	init(source: string, videoStart?: string, videoEnd?: string, autoplay = true, drmType?: DRMTYPE, laSource?: string, timeShift?: boolean): void {
		this.source = source;
		if (videoStart) this.videoStart = dayjs(videoStart).utc().format();
		if (videoEnd) this.videoEnd = dayjs(videoEnd).utc().format();
		this.autoplay = autoplay;
		this.drmType = drmType;
		this.laSource = laSource;
		this.timeShift = timeShift;

		this.load();
	}

	/**
	 * Loads the video player with the specified source configuration.
	 *
	 * The source configuration includes the HLS URL with optional start and end parameters.
	 * If DRM (Digital Rights Management) is enabled, the Widevine license acquisition URL is also included.
	 *
	 * Once the player is loaded, it will automatically start playing if the autoplay option is enabled.
	 *
	 * @remarks
	 * This method uses the `load` method of the player to load the video source configuration.
	 *
	 * @example
	 * ```typescript
	 * playerWrapper.load();
	 * ```
	 */
	load(): void {
		let sourceUrl = this.source;
		if (!this.drmType && (this.videoStart || this.videoEnd)) sourceUrl += `?start=${this.videoStart}&end=${this.videoEnd}`;
		else if (this.timeShift) sourceUrl = sourceUrl.replace(TIMESHIFT_4S, TIMESHIFT_4S_V2);

		const sourceConfig: SourceConfig = {
			hls: sourceUrl
		};

		if (this.drmType && this.laSource)
			sourceConfig.drm = {
				[this.drmType]: {
					LA_URL: this.laSource,
					videoRobustness: ROBUSTNESS,
					audioRobustness: ROBUSTNESS
				}
			};

		this.player.load(sourceConfig).then(() => {
			if (this.autoplay) this.player.play();
		});
	}

	/**
	 * Destroys the video player instance and releases any resources associated with it.
	 * This method should be called when the video player is no longer needed to ensure proper cleanup.
	 */
	destroy(): void {
		this.player.destroy();
	}
}
